import { supabase } from "../utils/supabaseClient";
import { PlanTier, ResourceAddonPack, ResourceType } from "../types/subscription";
import { ADMIN_CONFIG } from "../utils/constants";

export type CreateAddonPackData = {
  name: string;
  description?: string;
  resource_type_id: string;
  quantity: number;
  price?: number;
  is_active: boolean;
};

export type UpdateAddonPackData = Partial<CreateAddonPackData> & { id: string };

export interface CreatePlanTierData {
  name: string;
  description?: string;
  tier_level: number;
  price_monthly?: number;
  price_yearly?: number;
  is_active: boolean;
  features: Record<string, any>;
}

export interface UpdatePlanTierData extends Partial<CreatePlanTierData> {
  id: string;
}

export interface PlanTierFilters {
  is_active?: boolean;
  tier_level?: number;
  search?: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface PlanTierResponse {
  data: PlanTier[];
  count: number;
  error: string | null;
}

export class AdminService {
  // Get all plan tiers with filtering and pagination
  static async getPlanTiers(
    filters: PlanTierFilters = {},
    pagination: PaginationParams = {}
  ): Promise<PlanTierResponse> {
    try {
      const { page = 1, limit = ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE } =
        pagination;
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      let query = supabase
        .schema("altzero")
        .from("base_plan_tiers")
        .select("*", { count: "exact" })
        .order("tier_level", { ascending: true })
        .order("created_at", { ascending: false });

      // Apply filters
      if (filters.is_active !== undefined) {
        query = query.eq("is_active", filters.is_active);
      }

      if (filters.tier_level !== undefined) {
        query = query.eq("tier_level", filters.tier_level);
      }

      if (filters.search) {
        query = query.or(
          `name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
        );
      }

      // Apply pagination
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw error;
      }

      return {
        data: data || [],
        count: count || 0,
        error: null,
      };
    } catch (error) {
      console.error("Error fetching plan tiers:", error);
      return {
        data: [],
        count: 0,
        error:
          error instanceof Error ? error.message : "Failed to fetch plan tiers",
      };
    }
  }

  // Get a single plan tier by ID
  static async getPlanTier(
    id: string
  ): Promise<{ data: PlanTier | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .schema("altzero")
        .from("base_plan_tiers")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error fetching plan tier:", error);
      return {
        data: null,
        error:
          error instanceof Error ? error.message : "Failed to fetch plan tier",
      };
    }
  }

  // Create a new plan tier
  static async createPlanTier(
    planData: CreatePlanTierData
  ): Promise<{ data: PlanTier | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .schema("altzero")
        .from("base_plan_tiers")
        .insert([planData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error creating plan tier:", error);
      return {
        data: null,
        error:
          error instanceof Error ? error.message : "Failed to create plan tier",
      };
    }
  }

  // Update an existing plan tier
  static async updatePlanTier(
    updateData: UpdatePlanTierData
  ): Promise<{ data: PlanTier | null; error: string | null }> {
    try {
      const { id, ...updates } = updateData;

      const { data, error } = await supabase
        .schema("altzero")
        .from("base_plan_tiers")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error updating plan tier:", error);
      return {
        data: null,
        error:
          error instanceof Error ? error.message : "Failed to update plan tier",
      };
    }
  }

  // Delete a plan tier (soft delete by setting is_active to false)
  static async deletePlanTier(
    id: string
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase
        .schema("altzero")
        .from("base_plan_tiers")
        .update({
          is_active: false,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error("Error deleting plan tier:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to delete plan tier",
      };
    }
  }

  // Permanently delete a plan tier (use with caution)
  static async permanentlyDeletePlanTier(
    id: string
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase.schema("altzero").from("base_plan_tiers").delete().eq("id", id);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error("Error permanently deleting plan tier:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to permanently delete plan tier",
      };
    }
  }

  // Get plan tier statistics
  static async getPlanTierStats(): Promise<{
    data: {
      total: number;
      active: number;
      inactive: number;
    } | null;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .schema("altzero")
        .from("base_plan_tiers")
        .select("is_active");

      if (error) {
        throw error;
      }

      const stats = {
        total: data.length,
        active: data.filter((plan) => plan.is_active).length,
        inactive: data.filter((plan) => !plan.is_active).length,
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error("Error fetching plan tier stats:", error);
      return {
        data: null,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch plan tier statistics",
      };
    }
  }

  // ─── Resource Types ────────────────────────────────────────────────────────

  static async getResourceTypes(): Promise<{ data: ResourceType[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .schema("altzero")
        .from("base_resource_types")
        .select("*")
        .order("code", { ascending: true });
      if (error) throw error;
      return { data: data ?? [], error: null };
    } catch (error) {
      return { data: [], error: error instanceof Error ? error.message : "Failed to fetch resource types" };
    }
  }

  static async createResourceType(input: {
    code: string;
    name: string;
    description?: string;
    unit_label: string;
    value_type?: 'count' | 'flag' | 'link' | 'plugin';
  }): Promise<{ data: ResourceType | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .schema("altzero")
        .from("base_resource_types")
        .insert([input])
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : "Failed to create resource type" };
    }
  }

  // ─── Addon Packs ───────────────────────────────────────────────────────────

  static async getAddonPacks(): Promise<{ data: ResourceAddonPack[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .schema("altzero")
        .from("base_resource_addon_packs")
        .select(`*, resource_type:base_resource_types!resource_type_id(id, code, name, unit_label, created_at)`)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return { data: (data ?? []) as ResourceAddonPack[], error: null };
    } catch (error) {
      return { data: [], error: error instanceof Error ? error.message : "Failed to fetch addon packs" };
    }
  }

  static async createAddonPack(packData: CreateAddonPackData): Promise<{ data: ResourceAddonPack | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .schema("altzero")
        .from("base_resource_addon_packs")
        .insert([packData])
        .select(`*, resource_type:base_resource_types!resource_type_id(id, code, name, unit_label, created_at)`)
        .single();
      if (error) throw error;
      return { data: data as ResourceAddonPack, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : "Failed to create addon pack" };
    }
  }

  static async updateAddonPack(updateData: UpdateAddonPackData): Promise<{ data: ResourceAddonPack | null; error: string | null }> {
    try {
      const { id, ...updates } = updateData;
      const { data, error } = await supabase
        .schema("altzero")
        .from("base_resource_addon_packs")
        .update(updates)
        .eq("id", id)
        .select(`*, resource_type:base_resource_types!resource_type_id(id, code, name, unit_label, created_at)`)
        .single();
      if (error) throw error;
      return { data: data as ResourceAddonPack, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : "Failed to update addon pack" };
    }
  }

  static async deleteAddonPack(id: string): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase
        .schema("altzero")
        .from("base_resource_addon_packs")
        .update({ is_active: false })
        .eq("id", id);
      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : "Failed to delete addon pack" };
    }
  }

  // ─── Validate plan tier data ───────────────────────────────────────────────

  // Validate plan tier data
  static validatePlanTierData(data: CreatePlanTierData | UpdatePlanTierData): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if ("name" in data && (!data.name || data.name.trim().length === 0)) {
      errors.push("Plan name is required");
    }

    if ("name" in data && data.name && data.name.trim().length > 100) {
      errors.push("Plan name must be less than 100 characters");
    }

    if (
      "tier_level" in data &&
      (data.tier_level === undefined || data.tier_level < 0)
    ) {
      errors.push("Tier level must be a non-negative number");
    }

    if (
      "features" in data &&
      data.features &&
      typeof data.features !== "object"
    ) {
      errors.push("Features must be a valid JSON object");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}
